import * as React from 'react';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { tokenService } from '../services/tokenService';
import axios from 'axios';
import baseURL from '@/http';
import { getValidToken } from '@/pages/public/Login/checkTokenValidity';
import { releaseServerSession } from '@/services/sessionSignout';
import { CONTEXT_SEEN_KEY } from '@/services/tenantClaims';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'student' | 'teacher' | 'admin' | 'institution_admin' | 'super_admin' | 'parent';
  institution_id?: string;
  subscription_plan: 'free' | 'basic' | 'premium' | 'enterprise';
  subscription_validity: string;
  permissions: string[];
  organizationName?: string;
  avatar?: string;
  parentEmail?: string;
  studentIds?: string[];
}

/** Raw response from /interlearn/role_permission_get/ stored for app-wide access */
export interface AccessData {
  user_email?: string;
  UserType?: string;
  permission_actions?: Record<string, string[]>;
  [key: string]: any;
}

export interface Subscription {
  plan: 'free' | 'basic' | 'premium' | 'enterprise';
  validity: string;
  maxUsers: number;
  maxSessions: number;
  features: string[];
  status: 'active' | 'expired' | 'cancelled' | 'trial';
}

interface AuthContextType {
  user: User | null;
  subscription: Subscription | null;
  isAuthenticated: boolean;
  /** Full AccessData object from role_permission_get API */
  accessData: AccessData | null;
  /** Logged-in user email from role_permission_get API */
  userEmail: string;
  /** Store accessData + userEmail into context and localStorage */
  setAccessData: (data: AccessData, email?: string) => void;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  hasPermission: (permission: string) => boolean;
  canAccessRole: (requiredRole: string | string[]) => boolean;
  isSubscriptionActive: () => boolean;
  getMaxUsers: () => number;
  getMaxSessions: () => number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const subscriptionPlans = {
  free: {
    maxUsers: 10,
    maxSessions: 5,
    features: ['basic_analytics', 'email_support'],
  },
  basic: {
    maxUsers: 100,
    maxSessions: 50,
    features: ['advanced_analytics', 'priority_support', 'batch_management'],
  },
  premium: {
    maxUsers: 500,
    maxSessions: -1, // unlimited
    features: ['white_label', 'api_access', 'custom_integrations'],
  },
  enterprise: {
    maxUsers: -1, // unlimited
    maxSessions: -1, // unlimited
    features: ['dedicated_support', 'on_premise', 'custom_development'],
  },
};

const roleHierarchy = {
  student: 1,
  parent: 2,
  teacher: 3,
  admin: 4,
  institution_admin: 5,
  super_admin: 6,
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessData, setAccessDataState] = useState<AccessData | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    // Check for existing session on mount
    const storedUser = localStorage.getItem('user');
    const authStatus = localStorage.getItem('isAuthenticated');

    if (storedUser && authStatus === 'true') {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        logout();
      }
    }

    // Also check if we have valid tokens but no user session
    if (!storedUser && tokenService.isAuthenticated()) {
      // We have tokens but no user data - this might be a token-only session
      setIsAuthenticated(true);
    }

    // Restore accessData and userEmail persisted by setAccessData
    const storedAccessData = localStorage.getItem('accessData');
    const storedUserEmail = localStorage.getItem('userEmail');
    if (storedAccessData) {
      try {
        setAccessDataState(JSON.parse(storedAccessData));
      } catch {
        // ignore corrupt data
      }
    }
    if (storedUserEmail) {
      setUserEmail(storedUserEmail);
    }

    // If accessData was not in localStorage but the user has a valid token,
    // fetch it from the API so the app has permissions loaded after a cold navigation.
    if (!storedAccessData && tokenService.isAuthenticated()) {
      (async () => {
        try {
          const token = await getValidToken();
          if (!token) return;
          const response = await axios.get(
            `${baseURL}/interlearn/role_permission_get/`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (response.data?.status === 'ok') {
            const data: AccessData = response.data;
            const email = data?.user_email ?? '';
            setAccessDataState(data);
            setUserEmail(email);
            // localStorage.setItem('accessData', JSON.stringify(data));
            // localStorage.setItem('userEmail', email);
          }
        } catch {
          // silently fail — app still works without accessData
        }
      })();
    }

    // Subscribe to token changes
    const unsubscribe = tokenService.subscribe((tokens) => {
      // If tokens are cleared, ensure auth state is also cleared
      if (!tokens.access_token && !tokens.auth_token) {
        setIsAuthenticated(false);
        setUser(null);
      }
    });

    return unsubscribe;
  }, []);

  const subscription: Subscription | null = user
    ? {
        plan: user.subscription_plan,
        validity: user.subscription_validity,
        maxUsers: subscriptionPlans[user.subscription_plan].maxUsers,
        maxSessions: subscriptionPlans[user.subscription_plan].maxSessions,
        features: subscriptionPlans[user.subscription_plan].features,
        status: new Date(user.subscription_validity) > new Date() ? 'active' : 'expired',
      }
    : null;

  const login = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isAuthenticated', 'true');
  };

  const logout = () => {
    // Tell the server FIRST, while the tokens that identify this session still
    // exist. Without this the account's concurrent-login slot stays claimed and
    // the next sign-in - from this very browser - is met with "Already signed
    // in elsewhere". Fire-and-forget: the request uses keepalive so it survives
    // the redirect below, and a failure must never hold up a logout.
    void releaseServerSession();

    // Clear tokens using the unified service
    tokenService.clearTokens();

    // Clear local state
    setUser(null);
    setIsAuthenticated(false);
    setAccessDataState(null);
    setUserEmail('');

    // Clear persisted accessData so it isn't loaded after a fresh login.
    localStorage.removeItem('accessData');
    localStorage.removeItem('userEmail');
    // The role goes with it: the next person to sign in on this browser must
    // not inherit the last one's screens.
    localStorage.removeItem('userType');

    // "This session once held a context claim." Must not outlive the session:
    // the next sign-in is claimless wherever MULTI_TENANT_CONTEXT_ENABLED is
    // off — production today — and a stale marker would make resolveInstituteId
    // read that as a revocation and drop the institute fallback entirely.
    localStorage.removeItem(CONTEXT_SEEN_KEY);

    // Redirect to our comprehensive SignOut component which handles:
    // - Complete token cleanup
    // - Visual feedback
    // - Secure redirection to home page
    window.location.href = '/signout';
  };

  const setAccessData = (data: AccessData, email?: string) => {
    const resolvedEmail = email ?? data?.user_email ?? '';
    setAccessDataState(data);
    setUserEmail(resolvedEmail);
    // Persist so accessData survives navigation and page refreshes.
    // localStorage.setItem('accessData', JSON.stringify(data));
    // localStorage.setItem('userEmail', resolvedEmail);

    // THE ROLE ALONE, and nothing else from this response.
    //
    // `learnerAudience` decides which of two correct screens to draw — the
    // learner's own library, or the management table — and it reads from
    // storage because the components that ask are mounted outside this
    // provider. It was reading the `accessData` key, which the two lines above
    // stopped writing: nothing has written it since, so every student has been
    // answered "not a learner" and served the staff screens, silently.
    //
    // Only the role is stored, deliberately. Persisting the whole permission
    // tree is what those lines were commented out to avoid — a stale tree
    // outliving the sign-in that earned it — and no permission decision is
    // taken from this: the sidebar filters from the live response, and the
    // server enforces the rest. The worst a tampered value does is show
    // somebody a screen whose buttons do not work for them.
    try {
      const role = typeof data?.UserType === 'string' ? data.UserType : '';
      if (role) localStorage.setItem('userType', role);
      else localStorage.removeItem('userType');
    } catch {
      // A private window that refuses storage: the audience falls back to the
      // staff screens, which is what everyone saw before this existed.
    }
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      // localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    // Super admin has all permissions
    if (user.role === 'super_admin') return true;

    // Institution admin has all institution-level permissions
    if (user.role === 'institution_admin' && permission.includes('institution')) return true;

    // Check specific permissions
    return user.permissions.includes(permission) || user.permissions.includes('all_permissions');
  };

  const canAccessRole = (requiredRole: string | string[]): boolean => {
    if (!user) return false;

    const requiredRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const userRoleLevel = roleHierarchy[user.role];

    return requiredRoles.some((role) => {
      const requiredLevel = roleHierarchy[role as keyof typeof roleHierarchy];
      return userRoleLevel >= requiredLevel;
    });
  };

  const isSubscriptionActive = (): boolean => {
    if (!subscription) return false;
    return subscription.status === 'active';
  };

  const getMaxUsers = (): number => {
    return subscription?.maxUsers || 0;
  };

  const getMaxSessions = (): number => {
    return subscription?.maxSessions || 0;
  };

  const value: AuthContextType = {
    user,
    subscription,
    isAuthenticated,
    accessData,
    userEmail,
    setAccessData,
    login,
    logout,
    updateUser,
    hasPermission,
    canAccessRole,
    isSubscriptionActive,
    getMaxUsers,
    getMaxSessions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Higher-order component for route protection
interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string | string[];
  requiredPermission?: string;
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallback = <div className="p-4 text-center">Access Denied: Insufficient permissions</div>,
}) => {
  const { user, canAccessRole, hasPermission, isSubscriptionActive } = useAuth();

  if (!user) {
    return <div className="p-4 text-center">Please log in to access this page</div>;
  }

  if (!isSubscriptionActive()) {
    return (
      <div className="p-4 text-center">
        Your subscription has expired. Please renew to continue.
      </div>
    );
  }

  if (requiredRole && !canAccessRole(requiredRole)) {
    return fallback;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback;
  }

  return <>{children}</>;
};

export default AuthProvider;
